import { RegisteredComponent } from "@builder.io/sdk-react";

export const sliderBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Slider",
  image:
    "https://icons.veryicon.com/png/o/miscellaneous/light-weight-line-icon/st-image-slider.png",
  canHaveChildren: true,
  description:
    "The Slider component wrapping the context for the Slides and Slider controls components.",
  docsLink: "https://react-slick.neostack.com/docs/api/",
  inputs: [
    {
      name: "autoScrollCarousel",
      type: "boolean",
      required: false,
      localized: false,
      defaultValue: false,
      helperText:
        "When enabled, this block renders Auto Scroll Carousel instead of the standard Slider.",
    },
    {
      name: "hasHeader",
      type: "boolean",
      required: false,
      localized: false,
      defaultValue: false,
      helperText:
        "When enabled, the first child block will be used as the header section displayed above the auto-scroll carousel. Only applicable when Auto Scroll Carousel is enabled.",
      showIf: (options) => options.get("autoScrollCarousel"),
    },
    {
      name: "autoplay",
      type: "boolean",
      required: false,
      localized: false,
      defaultValue: false,
    },
    {
      name: "autoplaySpeed",
      helperText: "Delay between each auto scroll (in milliseconds).",
      type: "number",
      required: false,
      localized: false,
      defaultValue: 3000,
      showIf: (options) => options.get("autoplay"),
    },
    {
      name: "infinite",
      type: "boolean",
      defaultValue: false,
      required: false,
      localized: false,
    },
    {
      name: "initialSlide",
      type: "number",
      defaultValue: 0,
      required: false,
      localized: false,
    },
    {
      name: "speed",
      type: "number",
      helperText: "Animation speed in milliseconds.",
      defaultValue: 500,
      required: false,
      localized: false,
    },
    {
      name: "vertical",
      type: "boolean",
      helperText: "Slide vertically instead of horizontally.",
      defaultValue: false,
      required: false,
      localized: false,
    },
    {
      name: "adaptiveHeight",
      type: "boolean",
      helperText: "Adjusts slider height to the current slide's height. Automatically sets slidesToShow: 1.",
      defaultValue: false,
      required: false,
      localized: false,
    },
  ],
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      children: [
        {
          "@type": "@builder.io/sdk:Element",
          children: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Left side - static content",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Lorem ipsum",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                },
              ],
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                },
              },
            },
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Right side - slides",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Slides",
                    options: {
                      slides: [
                        {
                          slide: [
                            {
                              "@type": "@builder.io/sdk:Element",
                              children: [
                                {
                                  "@type": "@builder.io/sdk:Element",
                                  meta: {
                                    localizedTextInputs: ["alt"],
                                  },
                                  component: {
                                    name: "Image",
                                    options: {
                                      source: "aprimo",
                                      alt: "Placeholder",
                                      image:
                                        "https://p1.aprimocdn.net/solarwinds/fe7b05e6-0071-4571-a052-b2f1016afa54/Placeholder_image_Original%20file.png",
                                    },
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        {
                          slide: [
                            {
                              "@type": "@builder.io/sdk:Element",
                              children: [
                                {
                                  "@type": "@builder.io/sdk:Element",
                                  meta: {
                                    localizedTextInputs: ["alt"],
                                  },
                                  component: {
                                    name: "Image",
                                    options: {
                                      source: "aprimo",
                                      alt: "Placeholder",
                                      image:
                                        "https://p1.aprimocdn.net/solarwinds/fe7b05e6-0071-4571-a052-b2f1016afa54/Placeholder_image_Original%20file.png",
                                    },
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  },
                  responsiveStyles: {
                    large: {
                      width: "100%",
                    },
                  },
                },
              ],
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexBasis: "0",
                  flexGrow: "1",
                  flexShrink: "1",
                  width: "100%",
                  minWidth: "0",
                },
              },
            },
          ],
          responsiveStyles: {
            large: {
              display: "flex",
              flexDirection: "row",
              columnGap: "var(--spacing-28, 112px)",
              width: "100%",
            },
          },
        },
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Slider controls",
          },
          responsiveStyles: {
            large: {
              display: "flex",
              flexDirection: "row",
              gap: "var(--spacing-6, 24px)",
            },
          },
        },
      ],
      responsiveStyles: {
        large: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
      },
    },
  ],
  defaultStyles: {
    width: "100%",
  },
};
